import type { ReactNode } from "react";
import { Copy, Eye } from "lucide-react";
import clsx from "clsx";
import { Icon } from "../Icon/Icon.js";
import styles from "./UrlBar.module.scss";

export interface UrlBarProps {
  /** The public URL to show. Rendered mono and ellipsised — the full value stays in `title`. */
  url: string;
  /**
   * Invoked when Copy is pressed. The caller owns the clipboard write and its toast, because the
   * toast host lives in the app, not here.
   */
  onCopy: () => void;
  copyLabel?: string;
  /** Set false for a URL that has nowhere to preview (a token that isn't a public page yet). */
  preview?: boolean;
  previewLabel?: string;
  /** Extra trailing controls (a Rotate, a Revoke). */
  actions?: ReactNode;
  /**
   * Stack the URL over a full-width action row instead of tucking the buttons inline at the end. For a
   * narrow surface (a phone venue card) where the URL and three actions cannot share one line legibly —
   * the buttons then split the row evenly beneath the address.
   */
  stack?: boolean;
  className?: string;
}

/**
 * The public-link well — the spec's `.lib__urlbar`: a pill holding the mono URL with its actions
 * tucked at the end (hence the asymmetric padding, roomy at the text end, tight at the button end).
 */
export function UrlBar({
  url,
  onCopy,
  copyLabel = "Copy",
  preview = true,
  previewLabel = "Preview",
  actions,
  stack = false,
  className,
}: UrlBarProps) {
  return (
    <div className={clsx(styles.bar, stack && styles.stack, className)}>
      <span className={styles.url} title={url}>
        {url}
      </span>
      <div className={styles.actions}>
        <button type="button" className={styles.btn} onClick={onCopy}>
          <Icon icon={Copy} size={16} /> {copyLabel}
        </button>
        {preview && (
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => window.open(url, "_blank", "noopener")}
          >
            <Icon icon={Eye} size={16} /> {previewLabel}
          </button>
        )}
        {actions}
      </div>
    </div>
  );
}
